// Initial Setup of connection to API
import React, { useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router-dom';
import axios from 'axios';

import Button from '@material-ui/core/Button';

import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';

// Posts the given global token to the auth server iframe
const setGlobalToken = token => {

    const i = document.getElementById('authFrame-CAPAT-global');

    i.contentWindow.postMessage(
        JSON.stringify({
            action: 'setGlobalToken',
            token: token,
            location: location.hostname
        }),
        process.env.MIX_AUTH_BASEURL
    );
}

// Sends a message to the auth server iframe that it wants the global token
const getGlobalToken = () => {

    const i = document.getElementById('authFrame-CAPAT-global');

    i.contentWindow.postMessage(
        JSON.stringify({
            action: 'getGlobalToken',
        }),
        process.env.MIX_AUTH_BASEURL
    );
}

// Listens for the post message from the auth server iframe and then returns
// token to resolve function if its set
const listenForGlobalToken = (resolve, reject) => {

    const handleMessage = event => {
        if (event.origin == process.env.MIX_AUTH_BASEURL) {
            let token = null;
            const data = JSON.parse(event.data);

            if (data && data.token && data.token !== null ) {
                window.removeEventListener("message", handleMessage);
                resolve(data.token);
            } else {
                reject(data);
            }
        }
    }

    window.addEventListener("message", handleMessage);
};

const listenForGlobalTokenAndGet = (resolve, reject) => {

    const handleMessage = event => {
        if (event.origin == process.env.MIX_AUTH_BASEURL) {
            let token = null;
            const data = JSON.parse(event.data);

            if (data && data.token && data.token !== null ) {
                window.removeEventListener("message", handleMessage);
                resolve(data.token);
            } else {
                reject(data);
            }
        }
    }

    window.addEventListener("message", handleMessage);

    getGlobalToken();
};

// Loads the auth server iframe. Needs to be called before any other functions will work properly
const loadGlobalTokenFrame = () => {

    const i = document.createElement('iframe');
    i.id = 'authFrame-CAPAT-global'
    i.style.display = 'none';
    i.src = process.env.MIX_AUTH_BASEURL + '/authenticate';
    document.body.appendChild(i);

}

export default function AppConnect(props) {

    let attemptingRefresh = false;
    let failedQueue = [];

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const { app, handleApp, hydrateApp } = useApp();

    const api = useApi();
    let history = useHistory();

    const processQueue = (error, token = null) => {

        failedQueue.forEach(prom => {
            if (error) {
                prom.reject(error);
            } else {
                prom.resolve(token);
            }
        })

        failedQueue = [];
    }

    const getSnackbarAction = sb => {
        if (sb.action) {
            switch (sb.action) {
                case 'refresh':
                    return (
                        <Button color="inherit" onClick={() => window.location.reload()}>
                            Refresh
                        </Button>
                    );
                case 'download':
                    return (
                        <Button color="inherit" component="a" download href={sb.url} target="_blank">
                            Download
                        </Button>
                    )
                default: return null;
            }
        }
        return null;
    }

    const getSnackbarDuration = sb => {
        if (sb.action) {
            switch (sb.action) {
                case 'refresh':
                    return 7000
                case 'download':
                    return 10000
                default: return 5000;
            }
        }
        return 5000;
    }

    const authorizeAxios = (token = null) => {
        if (token) {
            api.defaults.withCredentials = true;
            api.defaults.headers.common['Authorization'] = 'Bearer ' + token;
        } else {
            api.defaults.withCredentials = false;
            api.defaults.headers.common['Authorization'] = null;
        }
    }

    const handleTokens = (tokens = null) => {
        if (tokens !== null && typeof tokens === 'object') {
            setGlobalToken(tokens.refresh_token);
            authorizeAxios(tokens.access_token);
        } else {
            setGlobalToken(null);
            authorizeAxios(null);
        }
    }

    const handleTokenError = (data) => {
        if (data.error) {
            switch (data.error) {
                case 'cookie-error':
                    const action = key => (
                        <Button onClick={() => closeSnackbar(key)} color="inherit">
                            Dismiss
                        </Button>
                    );

                    enqueueSnackbar(
                        "Please enable third-party cookies in your browser's security settings in order to stay logged in.", {
                            variant: 'warning',
                            autoHideDuration: 30000,
                            anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
                            persist: true,
                            action
                        }
                    );
                    break;
            }
        }
    }

    const attachTokenToRequest = (request, token) => {
        request.withCredentials = true;
        request.headers['Authorization'] = 'Bearer ' + token;
    }

    const setApiBaseUrl = () => {
        api.defaults.baseURL = process.env.MIX_API_BASEURL;
    }

    const setApiDefaults = () => {
        api.defaults.headers.common['Accept'] = 'application/json';
        api.defaults.headers.common['Content-Type'] = 'application/json';
        api.defaults.headers.common['x-ca-origin'] = window.location.origin
    }

    const setApiInterceptors = () => {
        api.interceptors.response.use( response => {

            // Login on 234 response
            if (response.status == 234 && response.data.access_token) {
                handleTokens(response.data);
                hydrateApp();
            }

            // Logout on 235 response
            if (response.status == 235) {
                handleTokens(null);
                hydrateApp();
                history.push('/login');
            }

            // If the hydrate key exists, pass it to the hydrate function...
            if (response.data.hasOwnProperty('hydrate')) hydrateApp(response.data.hydrate);

            if (response.data.snackbar) {
                const sb = response.data.snackbar;

                enqueueSnackbar(
                    sb.text, {
                        variant: sb.variant,
                        action: getSnackbarAction(sb),
                        autoHideDuration: getSnackbarDuration(sb),
                        preventDuplicate: false
                    });
            }

            if (response.data instanceof Blob) return response.data;

            if (response.data.data) return response.data.data;

        }, error => {

            if (error.response) {
                switch (error.response.status) {
                    // Not authenticated
                    case 401:
                        // Check if error is a retry of original request or is from the queue
                        if (error.config._retry || error.config._queued) {
                            Promise.reject(error);
                        }

                        const originalRequest = error.config;

                        if (attemptingRefresh) {
                            return new Promise((resolve, reject) => {
                                failedQueue.push({resolve, reject});
                            }).then(token => {
                                originalRequest._queued = true;
                                attachTokenToRequest(originalRequest, token);
                                return api.request(originalRequest);
                            }).catch(() => {
                                return Promise.reject(error)
                            })
                        }

                        attemptingRefresh = true;
                        originalRequest._retry = true;

                        return new Promise((resolve, reject) => {
                            return new Promise(listenForGlobalTokenAndGet).then(token => {
                                refreshAccessToken(token).then(res => {
                                    handleTokens(res.data.data);
                                    attachTokenToRequest(originalRequest, res.data.data.access_token);
                                    processQueue(null, res.data.data.access_token);
                                    resolve(api.request(originalRequest));
                                }).catch(() => {
                                    reject(error);
                                });
                            }).catch(err => {
                                reject(error);
                            });
                        }).catch(err => {
                            processQueue(err, null);
                            handleTokens(null);
                            hydrateApp();
                        }).finally(() => {
                            attemptingRefresh = false;
                        });

                        break;

                    // Not an authorized action
                    case 403:
                        let sba = error.response.data.snackbar;
                        enqueueSnackbar(sba.text, {variant: sba.variant});
                        break;

                    // Route / Method Errors
                    case 404:
                    case 405:
                        enqueueSnackbar('Route Not Found', {variant: 'error'});
                        break;

                    // Stripe Errors
                    case 480:
                    case 481:
                    case 482:
                    case 483:
                    case 484:
                        let sbb = error.response.data.snackbar;
                        enqueueSnackbar(sbb.text, {variant: sbb.variant});
                        break;

                    // File size too large
                    case 413:
                        enqueueSnackbar(
                            "The file/image uploaded was too large. Please use a smaller file.", {
                                variant: 'error'
                            }
                        );
                        break;

                    // Not a member of the club, rehydrate to send to auth page
                    case 419:
                        //window.location = process.env.MIX_HOME_BASEURL + '/?caqe=904';
                        hydrateApp();
                        break;

                    // Club does not exist
                    case 420:
                        window.location = process.env.MIX_HOME_BASEURL + '/?caqe=905';
                        break;

                    // Validation Error
                    case 422:
                        const errors = error.response.data.errors
                        Object.keys(errors).map(function(i) {
                            if (! errors[i]?.quiet) enqueueSnackbar(errors[i][0], {variant: 'error'});
                        });
                        break;

                    // Too many requests via throttle middleware
                    case 429:
                        enqueueSnackbar(
                            "Too many requests. Please try again later.", {
                                variant: 'error'
                            }
                        );
                        break;

                    // Refresh token didn't work - Handled per refresh request but keep this here as to not return an error in promise
                    case 435: break;

                    // User Registered successfully but error when logging in - redirect to login screen
                    case 436:
                        enqueueSnackbar(
                            "Your profile was created successfully, but we could not log you in.", {
                                variant: 'error'
                            }
                        );
                        history.push('/login');
                        break;

                    // Quickbook redirection to auth (redirect the error message as url)
                    case 470:
                        window.location = error.response.data.message;
                        break;

                    default:
                        enqueueSnackbar(
                            "An unknown error has occured. Please try again.", {
                                variant: 'error'
                            }
                        );
                }
            } else if (error.request) {
                enqueueSnackbar(
                    "Looks like you have been disconnected from the internet. Please reconnect and try again.", {
                        variant: 'error'
                    }
                );
            }

            return Promise.reject(error);
        });
    }

    const refreshAccessToken = (token = null) => {
        return axios.post(process.env.MIX_WEB_BASEURL + '/refresh', {refresh_token: token});
    }

    useEffect(() => {
        // Set Default Axios Properties
        setApiDefaults();
        setApiBaseUrl();
        setApiInterceptors();

        // Setup Initial Global Token Listener
        new Promise(listenForGlobalToken).then(token => {
            refreshAccessToken(token).then(res => {
                handleTokens(res.data.data);
            }).catch(() => {
                handleTokens(null);
            }).then(() => {
                hydrateApp();
            });
        }).catch(error => {
            handleTokenError(error);
            handleTokens(null);
            hydrateApp();
        });

        // Load the Global Token iFrame and Script
        loadGlobalTokenFrame();

    }, [])

    return props.children;
}
